import { saveAs } from 'file-saver';
import isEqual from 'lodash/isEqual';

import { showToast } from '~/components/App/toast/showToast';
import { FileMimeType } from '~/enums/FileMimeType';
import { INFO } from '~/types/toast.types';

export const exportCsvBlob = (file: BlobPart, name: string) => {
  const blob = new Blob([file], { type: 'text/csv' });
  saveAs(blob, name);
};

export const algorithmStatusHelper = (
  algoStatus?: Record<string, object> | null,
) => {
  return algoStatus
    ? Object.keys(algoStatus).map((algo) => {
        return {
          name: algo,
          ...algoStatus[algo],
        };
      })
    : null;
};

export const debounce = <Args extends unknown[]>(
  fn: (...args: Args) => void,
  time: number,
) => {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  return function (this: unknown, ...args: Args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), time);
  };
};

async function filter<T>(lst: T[], callback: (item: T) => Promise<boolean>) {
  const fail = Symbol();
  const res = await Promise.all(
    lst.map(async (item) => ((await callback(item)) ? item : fail)),
  );
  return res.filter((i): i is Awaited<T> => i !== fail);
}

/**
 * Filter for valid files (exclude folders i.e. `type === ''`)
 * @param files accepts a FileList or array of File
 * @returns filtered array of files
 */
export const filterValidFiles = async (files: FileList | File[]) => {
  /* Any files with 0 byte size are invalid */
  const nonEmptyFiles = [...files].filter((file) => file.size > 0);

  /*
   * We cannot rely on file.type, since a file without
   * an extension will have a type of "". However, a
   * folder named "someFolder.jpg" will also have its
   * type as image/jpeg.
   */
  if (Blob.prototype.hasOwnProperty('arrayBuffer')) {
    return await filter(nonEmptyFiles, async (file) => {
      try {
        const blob = file.slice(0, 1);
        await blob.arrayBuffer();
        return true;
      } catch {
        return false;
      }
    });
  }
  // fallback to file type if blob.arrayBuffer is not supported
  return await filter(nonEmptyFiles, (file) =>
    Promise.resolve(
      (Object.values(FileMimeType) as string[]).includes(file.type),
    ),
  );
};

// RFC 5322 official standard: https://emailregex.com/
export const isEmailValid = (emailString: string) =>
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    emailString,
  );

function onCopySuccess(
  infoMessage: string | undefined,
  callback: ((message?: string) => void) | null | undefined,
  displayToast: boolean,
) {
  if (!displayToast) return;
  if (callback) {
    callback(infoMessage);
  } else showToast({ status: 'informational', text: infoMessage ?? '' });
}

export const copyToClipboard = (
  stringToCopy: string,
  infoMessage?: string,
  icon: string = INFO,
  callback?: ((message?: string) => void) | null,
  displayToast = true,
) =>
  navigator.clipboard
    .writeText(stringToCopy)
    .then(() => onCopySuccess(infoMessage, callback, displayToast));

export const testIsDirty = (oldValue: unknown, newValue: unknown) => {
  return !isEqual(oldValue, newValue);
};
